import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { useWebSocket } from "../hooks/useWebSocket";
import {
  WebSocketMessage,
  WebSocketState,
  WebSocketConfig,
  MessageType,
  InitiativeEntry,
} from "../types/websocket";
import { WebSocketService } from "../services/websocketService";
import { UserService, UserDetails } from "../services/userService";

const hasBooleanProperty = <TKey extends string>(
  value: unknown,
  key: TKey
): value is Record<string, unknown> & Record<TKey, boolean> => {
  return (
    typeof value === "object" &&
    value !== null &&
    key in value &&
    typeof (value as Record<string, unknown>)[key] === "boolean"
  );
};

interface WebSocketContextType {
  // Connection state
  state: WebSocketState;
  isConnected: boolean;
  messages: WebSocketMessage[];
  lastMessage: WebSocketMessage | null;
  websocketService: WebSocketService | null;

  // Connection management
  connect: () => Promise<void>;
  disconnect: () => void;
  updateSessionId: (_sessionId: string) => void;

  // Message sending
  sendMessage: (
    _type: MessageType,
    _data: unknown,
    _recipients?: string[],
    _sessionId?: string
  ) => boolean;
  sendChatMessage: (
    _message: string,
    _messageType?: string,
    _characterName?: string,
    _recipientId?: string,
    _sessionId?: string
  ) => boolean;
  sendDiceRoll: (
    _formula: string,
    _rollName?: string,
    _isPrivate?: boolean,
    _characterId?: string,
    _sessionId?: string,
    _characterName?: string
  ) => boolean;
  shareAsset: (
    _assetId: string,
    _assetName: string,
    _assetType: string,
    _permissions?: string[],
    _sessionId?: string
  ) => boolean;
  sendPing: () => boolean;

  // Session management
  joinSession: (_sessionId: string, _characterId?: string, _characterName?: string) => boolean;
  leaveSession: (_sessionId: string) => boolean;

  // Initiative and combat management
  sendInitiativeUpdate: (
    _sessionId: string,
    _entries: InitiativeEntry[],
    _currentTurn: number,
    _round: number,
    _isTracking: boolean
  ) => boolean;
  addToInitiative: (_sessionId: string, _entry: InitiativeEntry) => boolean;
  removeFromInitiative: (_sessionId: string, _entryId: string) => boolean;
  changeTurn: (_sessionId: string, _direction: "next" | "prev") => boolean;
  updateCombatState: (_sessionId: string, _state: string) => boolean;
  updateHealth: (
    _sessionId: string,
    _entryId: string,
    _hp: number,
    _maxHp: number,
    _change: number,
    _changeType: "damage" | "healing"
  ) => boolean;
  updateStatusEffects: (
    _sessionId: string,
    _entryId: string,
    _conditions: string[],
    _added?: string[],
    _removed?: string[]
  ) => boolean;
  sendSessionControl: (_sessionId: string, _action: string) => boolean;

  // User management
  getUserUsername: (_userId: string) => string | null;
  refreshUserCache: (_userIds: string[]) => Promise<void>;

  // Message management
  clearMessages: () => void;
  prependMessages: (_messages: WebSocketMessage[]) => void;
  getMessagesByType: (_messageType: MessageType, _sessionId?: string) => WebSocketMessage[];
  getLogMessages: (_sessionId?: string) => WebSocketMessage[];
  getChatMessages: (_sessionId?: string) => WebSocketMessage[];
  getDiceRolls: (_sessionId?: string) => WebSocketMessage[];
  getSystemNotifications: (_sessionId?: string) => WebSocketMessage[];

  // Connection info
  connectionInfo: { state: WebSocketState; reconnectAttempts: number; sessionId?: string };
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
  config: WebSocketConfig;
  autoConnect?: boolean;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  config,
  autoConnect = true,
}) => {
  const [notifications, setNotifications] = useState<WebSocketMessage[]>([]);
  const [userCache, setUserCache] = useState<Map<string, UserDetails>>(new Map());

  const {
    state,
    isConnected,
    messages,
    lastMessage,
    websocketService,
    connect,
    disconnect,
    sendMessage,
    sendChatMessage,
    sendDiceRoll,
    shareAsset,
    sendPing,
    joinSession,
    leaveSession,
    clearMessages,
    prependMessages,
    updateSessionId,
    connectionInfo,
    sendInitiativeUpdate,
    addToInitiative,
    removeFromInitiative,
    changeTurn,
    updateCombatState,
    updateHealth,
    updateStatusEffects,
    sendSessionControl,
  } = useWebSocket(config, {
    autoConnect,
    onMessage: (message) => {
      // Handle special message types
      if (message.type === "system_alert" || message.type === "error") {
        setNotifications((prev) => [...prev, message]);
      }
    },
    onStateChange: (newState) => {
      if (newState === WebSocketState.CONNECTED) {
        // WebSocket connected - ready for session joining
      }
    },
    onError: (error) => {
      console.error("❌ WebSocket connection error:", error);
    },
  });

  // Message filtering utilities
  const getMessagesByType = (messageType: MessageType, sessionId?: string): WebSocketMessage[] => {
    return messages.filter((message) => {
      const typeMatch = message.type === messageType;
      const sessionMatch = !sessionId || String(message.sessionId) === String(sessionId);
      return typeMatch && sessionMatch;
    });
  };

  const getChatMessages = (sessionId?: string): WebSocketMessage[] => {
    const chatTypes: MessageType[] = [
      "chat_message",
      "chat_ic",
      "chat_ooc",
      "chat_whisper",
      "chat_system",
    ];
    return messages.filter((message) => {
      const typeMatch = chatTypes.includes(message.type);
      const sessionMatch = !sessionId || String(message.sessionId) === String(sessionId);
      return typeMatch && sessionMatch;
    });
  };

  const getDiceRolls = (sessionId?: string): WebSocketMessage[] => {
    const diceTypes: MessageType[] = ["dice_roll", "initiative_roll", "skill_check"];
    return messages.filter((message) => {
      const typeMatch = diceTypes.includes(message.type);
      const sessionMatch = !sessionId || String(message.sessionId) === String(sessionId);
      return typeMatch && sessionMatch;
    });
  };

  const getLogMessages = (sessionId?: string): WebSocketMessage[] => {
    const logTypes: MessageType[] = ["chat_ic", "dice_roll", "chat_system", "system_alert"];
    return messages.filter((message) => {
      const typeMatch = logTypes.includes(message.type);
      const sessionMatch = !sessionId || String(message.sessionId) === String(sessionId);

      // Special handling for dice rolls: only include non-private ones in the log
      if (message.type === "dice_roll") {
        const isPrivate =
          hasBooleanProperty(message.data, "isPrivate") && message.data.isPrivate === true;
        return typeMatch && sessionMatch && !isPrivate;
      }

      return typeMatch && sessionMatch;
    });
  };

  const getSystemNotifications = (sessionId?: string): WebSocketMessage[] => {
    const systemTypes: MessageType[] = [
      "system_alert",
      "error",
      "user_joined",
      "user_left",
      "session_state",
    ];
    return messages.filter((message) => {
      const typeMatch = systemTypes.includes(message.type);
      const sessionMatch = !sessionId || String(message.sessionId) === String(sessionId);
      return typeMatch && sessionMatch;
    });
  };

  // User management functions
  const getUserUsername = (userId: string): string | null => {
    const user = userCache.get(userId);
    return user?.username || null;
  };

  const refreshUserCache = async (userIds: string[]): Promise<void> => {
    try {
      const uncachedUserIds = userIds.filter((id) => !userCache.has(id));
      if (uncachedUserIds.length === 0) return;

      const numericUserIds = uncachedUserIds.map((id) => parseInt(id)).filter((id) => !isNaN(id));
      if (numericUserIds.length === 0) return;

      const users = await UserService.getUsersByIds(numericUserIds);

      setUserCache((prev) => {
        const newCache = new Map(prev);
        users.forEach((user) => {
          newCache.set(user.id.toString(), user);
        });
        return newCache;
      });
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  };

  // Clear old notifications after a delay
  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setTimeout(() => {
        setNotifications((prev) => prev.slice(-10)); // Keep only last 10 notifications
      }, 30000); // Clear after 30 seconds

      return () => clearTimeout(timer);
    }
  }, [notifications.length]);

  const contextValue: WebSocketContextType = {
    state,
    isConnected,
    messages,
    lastMessage,
    websocketService,
    connect,
    disconnect,
    updateSessionId,
    sendMessage,
    sendChatMessage,
    sendDiceRoll,
    shareAsset,
    sendPing,
    joinSession,
    leaveSession,
    sendInitiativeUpdate,
    addToInitiative,
    removeFromInitiative,
    changeTurn,
    updateCombatState,
    updateHealth,
    updateStatusEffects,
    sendSessionControl,
    getUserUsername,
    refreshUserCache,
    clearMessages,
    prependMessages,
    getMessagesByType,
    getLogMessages,
    getChatMessages,
    getDiceRolls,
    getSystemNotifications,
    connectionInfo,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

// Hook to use WebSocket context
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};

/** Returns the WebSocket context or null if not inside a provider. Safe to call outside sessions. */
export const useOptionalWebSocketContext = (): WebSocketContextType | null => {
  return useContext(WebSocketContext);
};

// Hook for checking if WebSocket is available and connected
export const useWebSocketStatus = () => {
  const { state, isConnected, connectionInfo } = useWebSocketContext();

  return {
    isAvailable: state !== WebSocketState.DISCONNECTED,
    isConnected,
    isConnecting: state === WebSocketState.CONNECTING,
    hasError: state === WebSocketState.ERROR,
    reconnectAttempts: connectionInfo.reconnectAttempts,
    sessionId: connectionInfo.sessionId,
  };
};
